/**
 * CheaperThere | Cheap Flight & Hotel Deals
 *
 * Declares one web-accessible-resource group — `interceptor.js`, `img/*` and `fonts/*` —
 * matched to every origin with no `use_dynamic_url`. So its injected interceptor script,
 * the airline-logo images and the bundled fonts are all fetchable from any page at a
 * fixed URL that carries the extension ID.
 *
 * `interceptor.js` is the most distinctive stable path; the airline logos load through
 * `probeImage` with no CORS grant, corroborating even under a strict page CSP.
 *
 * Manifest verified against version 4.7.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ickdpignpmjcahkjfbmfljhiglmcmchn';

export const cheaperthere: ExtensionDefinition = {
  id: ID,
  name: 'CheaperThere | Cheap Flight & Hotel Deals',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 20_000,
  notes:
    'interceptor.js, img/ and fonts/ exposed to every origin with no dynamic URL. ' +
    'The interceptor script is a single conclusive fetch; airline logos corroborate CORS-free.',
  signals: [
    {
      id: 'war-interceptor-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch interceptor.js — the vendor content script exposed to every origin, present ' +
        'only when the extension is installed.',
      weight: 1,
      run: () => probeFetch(ID, 'interceptor.js'),
    },
    {
      id: 'war-airline-logo-image',
      kind: 'web-accessible-resource',
      description:
        'Load img/airlines/0B.png via <img> — an airline-logo image at a stable IATA-code ' +
        'path; loads without a CORS grant, so it works under a strict page CSP.',
      weight: 0.5,
      run: () => probeImage(ID, 'img/airlines/0B.png'),
    },
    {
      id: 'war-heebo-font',
      kind: 'web-accessible-resource',
      description: 'Fetch fonts/Heebo-Regular.ttf — a bundled font, a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'fonts/Heebo-Regular.ttf'),
    },
  ],
};
